/**
 * Services API v2 - Service Layer
 *
 * Provides methods for the new service management infrastructure:
 * - Service Categories (global reference data)
 * - Master Services (service definitions per installer)
 * - Installer Services (pricing per installer/size/catalog)
 * - Service Overrides (hierarchy relationships)
 */

import { HTTPService } from './httpService';

const API = new HTTPService();

// ============================================================================
// SERVICE CATEGORIES
// ============================================================================

/**
 * Get all service categories
 * @param {boolean} includeInactive - Include inactive categories
 * @returns {Promise<Array>} List of categories
 */
export const getServiceCategories = async (includeInactive = false) => {
  const params = includeInactive ? { includeInactive: true } : {};
  const { data } = await API.get('/api/service-categories', params);
  return data.data || data;
};

/**
 * Get a single service category by ID
 * @param {number} categoryId
 * @returns {Promise<Object>} Category object
 */
export const getServiceCategory = async (categoryId) => {
  const { data } = await API.get(`/api/service-categories/${categoryId}`);
  return data.data || data;
};

/**
 * Create a new service category
 * @param {Object} categoryData
 * @param {string} categoryData.categoryName - Display name (e.g. "Paint Protection")
 * @param {string} categoryData.installerCategory - Installer grouping key (e.g. "paint")
 * @param {number} [categoryData.displayOrder] - Sort order (defaults to 0)
 * @param {number} [categoryData.active] - 0 or 1 (defaults to 1)
 * @returns {Promise<Object>} { success: true, id: number }
 */
export const createServiceCategory = async (categoryData) => {
  const { data } = await API.post('/api/service-categories', categoryData);
  return data;
};

/**
 * Get categories for a specific installer category (Paint, Detail, PDR, etc.)
 * @param {string} installerCategory
 * @returns {Promise<Array>} List of categories
 */
export const getCategoriesByInstallerCategory = async (installerCategory) => {
  const { data } = await API.get(`/api/service-categories/installer-category/${installerCategory}`);
  return data.data || data;
};

// ============================================================================
// MASTER SERVICES (Definitions)
// ============================================================================

/**
 * Get a single master service by ID
 * @param {number} serviceId
 * @returns {Promise<Object>} Master service with category info
 */
export const getMasterService = async (serviceId) => {
  const { data } = await API.get(`/api/master-services/${serviceId}`);
  return data.data || data;
};

/**
 * Get all master services for an installer
 * @param {number} installerId
 * @returns {Promise<Array>} List of master services
 */
export const getMasterServicesByInstaller = async (installerId) => {
  const { data } = await API.get(`/api/master-services/installer/${installerId}`);
  return data.data || data;
};

/**
 * Get master services for an installer filtered by category
 * @param {number} installerId
 * @param {number} categoryId
 * @returns {Promise<Array>} List of master services
 */
export const getMasterServicesByCategory = async (installerId, categoryId) => {
  const { data } = await API.get(`/api/master-services/installer/${installerId}/category/${categoryId}`);
  return data.data || data;
};

/**
 * Create a new master service
 * @param {Object} serviceData
 * @param {number} serviceData.installerId
 * @param {number} serviceData.categoryId
 * @param {string} serviceData.serviceName
 * @param {string} [serviceData.coverageLevel] - FRONT | REAR | FULL | null
 * @param {string} [serviceData.dataCollectRequired] - Y | N (default Y)
 * @param {string} [serviceData.notes]
 * @returns {Promise<Object>} { success: true, id: number }
 */
export const createMasterService = async (serviceData) => {
  const { data } = await API.post('/api/master-services', serviceData);
  return data;
};

/**
 * Update a master service definition
 * @param {number} serviceId
 * @param {Object} updates - Fields to update (serviceName, coverageLevel, notes, dataCollectRequired, categoryId)
 * @returns {Promise<Object>} { success: true, message: string }
 */
export const updateMasterService = async (serviceId, updates) => {
  const { data } = await API.put(`/api/master-services/${serviceId}`, updates);
  return data;
};

/**
 * Delete (deactivate) a master service
 * @param {number} serviceId
 * @returns {Promise<Object>} { success: true, message: string }
 */
export const deleteMasterService = async (serviceId) => {
  const { data } = await API.delete(`/api/master-services/${serviceId}`);
  return data;
};

// ============================================================================
// SERVICE OVERRIDES (Hierarchy)
// ============================================================================

/**
 * Get services that a master service overrides/includes
 * @param {number} serviceId
 * @returns {Promise<Array>} List of overridden services
 */
export const getServiceOverrides = async (serviceId) => {
  const { data } = await API.get(`/api/master-services/${serviceId}/overrides`);
  return data.data || data;
};

/**
 * Add an override relationship (service A includes/overrides service B)
 * @param {number} serviceId - The service that overrides
 * @param {number} overriddenServiceId - The service being overridden
 * @returns {Promise<Object>} { success: true, id: number }
 */
export const addServiceOverride = async (serviceId, overriddenServiceId) => {
  const { data } = await API.post(`/api/master-services/${serviceId}/overrides`, {
    masterServiceId: overriddenServiceId  // API expects masterServiceId
  });
  return data;
};

/**
 * Remove an override relationship
 * @param {number} serviceId - The service that overrides
 * @param {number} overriddenServiceId - The service being overridden
 * @returns {Promise<Object>} { success: true, message: string }
 */
export const removeServiceOverride = async (serviceId, overriddenServiceId) => {
  const { data } = await API.delete(`/api/master-services/${serviceId}/overrides/${overriddenServiceId}`);
  return data;
};

// ============================================================================
// INSTALLER SERVICES (Pricing)
// ============================================================================

/**
 * Get all pricing records for an installer
 * @param {number} installerId
 * @param {Object} [filters] - Optional filters
 * @param {number} [filters.masterServiceId] - Filter by master service
 * @param {string} [filters.vehicleSize] - S | M | L
 * @param {string} [filters.catalog] - RETAIL | WHOLE
 * @param {string} [filters.installerCategory] - Paint | Detail | PDR
 * @returns {Promise<Array>} List of pricing records
 */
export const getInstallerServices = async (installerId, filters = {}) => {
  const { data } = await API.get(`/api/installer-services/installer/${installerId}`, filters);
  return data.data || data;
};

/**
 * Get pricing records for a specific master service (all 6 variants)
 * @param {number} installerId
 * @param {number} masterServiceId
 * @returns {Promise<Array>} List of 6 pricing records (S/M/L × RETAIL/WHOLE)
 */
export const getPricingForMasterService = async (installerId, masterServiceId) => {
  const { data } = await API.get(`/api/installer-services/installer/${installerId}`, {
    masterServiceId
  });
  return data.data || data;
};

/**
 * Create a single pricing record
 * @param {Object} pricingData
 * @returns {Promise<Object>} { success: true, id: number }
 */
export const createInstallerService = async (pricingData) => {
  const { data } = await API.post('/api/installer-services', pricingData);
  return data;
};

/**
 * Bulk create pricing records for a master service (all 6 variants)
 * @param {number} installerId
 * @param {number} masterServiceId
 * @param {Object} priceMatrix - Pricing by size and catalog
 * @param {Object} priceMatrix.S - { RETAIL: number, WHOLE: number, durationDays: number }
 * @param {Object} priceMatrix.M - { RETAIL: number, WHOLE: number, durationDays: number }
 * @param {Object} priceMatrix.L - { RETAIL: number, WHOLE: number, durationDays: number }
 * @returns {Promise<Object>} { success: true, insertedRows: 6 }
 */
export const bulkCreatePricing = async (installerId, masterServiceId, priceMatrix) => {
  const { data } = await API.post('/api/installer-services/bulk', {
    installerId,
    masterServiceId,
    priceMatrix
  });
  return data;
};

/**
 * Update a pricing record by its ID
 * @param {number} installerServiceId
 * @param {Object} updates - { price, durationDays }
 * @returns {Promise<Object>} { success: true, message: string }
 */
export const updateInstallerService = async (installerServiceId, updates) => {
  const { data } = await API.put(`/api/installer-services/${installerServiceId}`, updates);
  return data;
};

/**
 * Update pricing by composite key (installerId + masterServiceId + vehicleSize + catalog)
 * @param {Object} params
 * @param {number} params.installerId
 * @param {number} params.masterServiceId
 * @param {string} params.vehicleSize - S | M | L
 * @param {string} params.catalog - RETAIL | WHOLE
 * @param {number} params.price
 * @param {number} [params.durationDays]
 * @returns {Promise<Object>} { success: true, message: string }
 */
export const updatePriceByCompositeKey = async (params) => {
  const { data } = await API.put('/api/installer-services/price', params);
  return data;
};

/**
 * Delete (deactivate) a pricing record
 * @param {number} installerServiceId
 * @returns {Promise<Object>} { success: true, message: string }
 */
export const deleteInstallerService = async (installerServiceId) => {
  const { data } = await API.delete(`/api/installer-services/${installerServiceId}`);
  return data;
};

// ============================================================================
// TRIM OVERRIDES (Vehicle-Specific Pricing)
// ============================================================================

/**
 * Get all trim overrides for a pricing record
 * @param {number} installerServiceId - The pricing record ID
 * @returns {Promise<Array>} List of trim overrides with vehicle info
 */
export const getTrimOverrides = async (installerServiceId) => {
  const { data } = await API.get(`/api/installer-services/${installerServiceId}/trim-overrides`);
  return data.data || data;
};

/**
 * Set or update a trim override price
 * @param {number} installerServiceId - The pricing record ID
 * @param {number} trimId - The vehicle trim ID (id_car_trim)
 * @param {number} overridePrice - The custom price
 * @returns {Promise<Object>} { success: true }
 */
export const setTrimOverride = async (installerServiceId, trimId, overridePrice) => {
  const { data } = await API.put(
    `/api/installer-services/${installerServiceId}/trim-overrides/${trimId}`,
    { overridePrice }
  );
  return data;
};

/**
 * Remove a trim override
 * @param {number} installerServiceId - The pricing record ID
 * @param {number} trimId - The vehicle trim ID
 * @returns {Promise<Object>} { success: true }
 */
export const removeTrimOverride = async (installerServiceId, trimId) => {
  const { data } = await API.delete(
    `/api/installer-services/${installerServiceId}/trim-overrides/${trimId}`
  );
  return data;
};

/**
 * Get all trim overrides for a master service (across all pricing records)
 * Convenience method that fetches pricing records first, then their overrides
 * @param {number} installerId
 * @param {number} masterServiceId
 * @returns {Promise<Object>} { retailOverrides: [], wholesaleOverrides: [] }
 */
export const getTrimOverridesForService = async (installerId, masterServiceId) => {
  // Get pricing records for this service
  const pricingRecords = await getInstallerServices(installerId, { masterServiceId });

  const retailOverrides = [];
  const wholesaleOverrides = [];

  // Fetch overrides for each pricing record
  for (const record of pricingRecords) {
    // API may return id or installer_service_id
    const recordId = record.id || record.installer_service_id;
    if (!recordId) continue; // Skip if no valid ID

    const overrides = await getTrimOverrides(recordId);

    // Add vehicle size and pricing record info to each override
    const enrichedOverrides = overrides.map(o => ({
      ...o,
      vehicleSize: record.vehicle_size,
      installerServiceId: recordId
    }));

    if (record.catalog === 'RETAIL') {
      retailOverrides.push(...enrichedOverrides);
    } else {
      wholesaleOverrides.push(...enrichedOverrides);
    }
  }

  return { retailOverrides, wholesaleOverrides };
};

/**
 * Set trim override for both RETAIL and WHOLESALE catalogs
 * @param {number} installerId
 * @param {number} masterServiceId
 * @param {number} trimId
 * @param {string} vehicleSize - S | M | L (from trim record)
 * @param {number} retailPrice - Custom retail price (null to skip)
 * @param {number} wholesalePrice - Custom wholesale price (null to skip)
 * @returns {Promise<Object>} { success: true }
 */
export const setTrimOverrideForService = async (
  installerId,
  masterServiceId,
  trimId,
  vehicleSize,
  retailPrice,
  wholesalePrice
) => {
  // Get pricing records for this service and size
  const pricingRecords = await getInstallerServices(installerId, {
    masterServiceId,
    vehicleSize
  });

  const results = [];

  for (const record of pricingRecords) {
    // API may return id or installer_service_id
    const recordId = record.id || record.installer_service_id;
    if (!recordId) continue;

    const price = record.catalog === 'RETAIL' ? retailPrice : wholesalePrice;

    if (price !== null && price !== undefined) {
      const result = await setTrimOverride(recordId, trimId, price);
      results.push(result);
    }
  }

  return { success: true, results };
};

/**
 * Get ALL trim overrides for an installer in a single call
 * Much more efficient than calling getTrimOverridesForService for each service
 * @param {number} installerId
 * @param {number} [trimId] - Optional: filter to a specific vehicle trim
 * @returns {Promise<Array>} List of all overrides with service and vehicle info
 */
export const getAllTrimOverrides = async (installerId, trimId = null) => {
  const params = trimId ? { trimId } : {};
  const { data } = await API.get(
    `/api/installer-services/installer/${installerId}/all-trim-overrides`,
    params
  );
  return data.data || data;
};

/**
 * Remove trim override from both RETAIL and WHOLESALE catalogs
 * @param {number} installerId
 * @param {number} masterServiceId
 * @param {number} trimId
 * @param {string} vehicleSize - S | M | L
 * @returns {Promise<Object>} { success: true }
 */
export const removeTrimOverrideForService = async (
  installerId,
  masterServiceId,
  trimId,
  vehicleSize
) => {
  // Get pricing records for this service and size
  const pricingRecords = await getInstallerServices(installerId, {
    masterServiceId,
    vehicleSize
  });

  const results = [];

  for (const record of pricingRecords) {
    // API may return id or installer_service_id
    const recordId = record.id || record.installer_service_id;
    if (!recordId) continue;

    try {
      const result = await removeTrimOverride(recordId, trimId);
      results.push(result);
    } catch (error) {
      // Ignore errors if override doesn't exist
      if (error.response?.status !== 404) {
        throw error;
      }
    }
  }

  return { success: true, results };
};

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Build a categories lookup object from the categories array
 * @param {Array} categories - Array of category objects
 * @returns {Object} Lookup by category_id
 */
export const buildCategoryLookup = (categories) => {
  const lookup = {};
  categories.forEach(cat => {
    lookup[cat.category_id] = {
      id: cat.category_id,
      name: cat.category_name,
      installerCategory: cat.installer_category,
      displayOrder: cat.display_order
    };
  });
  return lookup;
};

/**
 * Merge master services with their pricing records
 * @param {Array} masterServices - Array of master service definitions
 * @param {Array} installerServices - Array of pricing records
 * @returns {Array} Master services with nested pricing
 */
export const mergeServicesWithPricing = (masterServices, installerServices) => {
  // Group pricing by master_service_id
  const pricingByMasterId = {};
  installerServices.forEach(pricing => {
    const masterId = pricing.master_service_id;
    if (!pricingByMasterId[masterId]) {
      pricingByMasterId[masterId] = [];
    }
    pricingByMasterId[masterId].push(pricing);
  });

  // Merge into master services
  return masterServices.map(service => ({
    ...service,
    pricing: pricingByMasterId[service.id] || []
  }));
};

/**
 * Extract price matrix from pricing records (for edit form)
 * @param {Array} pricingRecords - Array of 6 pricing records
 * @returns {Object} { retailPrices: {L,M,S}, wholesalePrices: {L,M,S}, durations: {L,M,S} }
 */
export const extractPriceMatrix = (pricingRecords) => {
  const retailPrices = { L: 0, M: 0, S: 0 };
  const wholesalePrices = { L: 0, M: 0, S: 0 };
  const durations = { L: 0, M: 0, S: 0 };

  pricingRecords.forEach(record => {
    const size = record.vehicle_size;
    // Support both "price" (V2 spec) and "service_cost" (current API response)
    const price = record.price ?? record.service_cost ?? 0;

    if (record.catalog === 'RETAIL') {
      retailPrices[size] = price;
      durations[size] = record.duration_days || 0;
    } else if (record.catalog === 'WHOLE') {
      wholesalePrices[size] = price;
    }
  });

  return { retailPrices, wholesalePrices, durations };
};

/**
 * Build price matrix for bulk create API
 * @param {Object} retailPrices - { L, M, S }
 * @param {Object} wholesalePrices - { L, M, S }
 * @param {Object|number} durations - { L, M, S } or single number for all sizes
 * @returns {Object} priceMatrix for bulkCreatePricing
 */
export const buildPriceMatrix = (retailPrices, wholesalePrices, durations) => {
  const sizes = ['S', 'M', 'L'];
  const priceMatrix = {};

  sizes.forEach(size => {
    const durationDays = typeof durations === 'number' ? durations : (durations[size] || 0);
    priceMatrix[size] = {
      RETAIL: retailPrices[size] || 0,
      WHOLE: wholesalePrices[size] || 0,
      durationDays
    };
  });

  return priceMatrix;
};

/**
 * Filter available overlay candidates based on coverage compatibility
 * @param {Array} allServicesInCategory - All services in the same category
 * @param {Object} currentService - The service being edited
 * @returns {Array} Filtered list of valid overlay candidates
 */
export const filterOverlayCandidates = (allServicesInCategory, currentService) => {
  return allServicesInCategory.filter(service => {
    // Exclude self
    if (service.id === currentService.id) return false;

    const currentCoverage = currentService.coverage_level || currentService.coverageLevel;
    const candidateCoverage = service.coverage_level || service.coverageLevel;

    // Coverage compatibility rules
    if (currentCoverage === 'FULL') {
      // FULL can overlay FRONT, REAR, SIDE, ROOF, and other FULL
      return candidateCoverage === 'FRONT' || candidateCoverage === 'REAR' ||
             candidateCoverage === 'SIDE' || candidateCoverage === 'ROOF' ||
             candidateCoverage === 'FULL';
    }
    if (currentCoverage === 'FRONT') {
      // FRONT can only overlay other FRONT
      return candidateCoverage === 'FRONT';
    }
    if (currentCoverage === 'REAR') {
      // REAR can only overlay other REAR
      return candidateCoverage === 'REAR';
    }
    if (currentCoverage === 'SIDE') {
      // SIDE can only overlay other SIDE
      return candidateCoverage === 'SIDE';
    }
    if (currentCoverage === 'ROOF') {
      // ROOF can only overlay other ROOF
      return candidateCoverage === 'ROOF';
    }
    if (currentCoverage === 'INTERIOR') {
      // INTERIOR can only overlay other INTERIOR
      return candidateCoverage === 'INTERIOR';
    }
    if (currentCoverage === 'WHEEL') {
      // WHEEL can only overlay other WHEEL
      return candidateCoverage === 'WHEEL';
    }

    // No coverage level - no overlays
    return false;
  });
};

// Default export for convenience
const servicesApiV2 = {
  // Categories
  getServiceCategories,
  getServiceCategory,
  createServiceCategory,
  getCategoriesByInstallerCategory,

  // Master Services
  getMasterService,
  getMasterServicesByInstaller,
  getMasterServicesByCategory,
  createMasterService,
  updateMasterService,
  deleteMasterService,

  // Service Overrides (hierarchy)
  getServiceOverrides,
  addServiceOverride,
  removeServiceOverride,

  // Pricing
  getInstallerServices,
  getPricingForMasterService,
  createInstallerService,
  bulkCreatePricing,
  updateInstallerService,
  updatePriceByCompositeKey,
  deleteInstallerService,

  // Trim Overrides (vehicle-specific pricing)
  getTrimOverrides,
  setTrimOverride,
  removeTrimOverride,
  getTrimOverridesForService,
  setTrimOverrideForService,
  removeTrimOverrideForService,
  getAllTrimOverrides,

  // Helpers
  buildCategoryLookup,
  mergeServicesWithPricing,
  extractPriceMatrix,
  buildPriceMatrix,
  filterOverlayCandidates
};

export default servicesApiV2;
